"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Building2, MapPin, HardHat, FolderKanban, ArrowRight, ShieldCheck, PhoneCall } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
const relatedItems = [{
  id: "commercial",
  title: "Commercial scaffolding",
  description: "Perimeter access, stair towers, and temporary roofs for commercial sites and refurbishment projects.",
  badge: "Commercial",
  to: "/CommercialScaffoldingPage",
  iconName: "building"
}, {
  id: "areas",
  title: "Areas we cover",
  description: "Coverage across Berkshire, Surrey, Hampshire, Buckinghamshire, London, and the Home Counties.",
  badge: "Regional",
  to: "/AreasWeCoverPage",
  iconName: "map"
}, {
  id: "contractors",
  title: "Main contractors and developers",
  description: "Dedicated scaffolding management with full NASC and CHAS compliance on every handover.",
  badge: "Contractors",
  to: "/CommercialScaffoldingPage",
  iconName: "hardhat"
}, {
  id: "projects",
  title: "Projects and case studies",
  description: "Detailed view of recent scaffolding structures delivered for roofing, solar, and major builds.",
  badge: "Portfolio",
  to: "/ProjectsCaseStudiesPage",
  iconName: "folder"
}];
export default function RelatedServices() {
  const shouldReduceMotion = useReducedMotion();
  const getServiceIcon = (iconName: string) => {
    switch (iconName) {
      case "building":
        return <Building2 className="size-5 text-primary" />;
      case "map":
        return <MapPin className="size-5 text-primary" />;
      case "hardhat":
        return <HardHat className="size-5 text-primary" />;
      case "folder":
      default:
        return <FolderKanban className="size-5 text-primary" />;
    }
  };
  return <section id="relatedservices" data-nav="light" className="bg-background py-16 md:py-24 border-t border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="max-w-3xl mb-12">
          <Badge variant="outline" className="mb-3 px-3 py-1 font-medium">
            Additional solutions
          </Badge>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight text-foreground font-default">
            Explore related scaffolding services
          </h2>
          <p className="mt-3 text-base sm:text-lg text-muted-foreground font-default">
            Find access solutions, coverage details, and project examples built
            around confirmed safety standards.
          </p>
        </div>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {relatedItems.map((item, i) => <div key={item.id} data-index={i} className="h-full">
              <motion.div initial={shouldReduceMotion ? false : {
            opacity: 0,
            y: 16
          }} whileInView={shouldReduceMotion ? undefined : {
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            amount: 0.2
          }} transition={{
            duration: 0.4,
            delay: i * 0.08
          }} className="h-full">
                <Card className="h-full flex flex-col justify-between border-border bg-card shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-200 hover:border-primary/40 group">
                  <CardHeader className="pb-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="p-2.5 rounded-md bg-muted text-foreground flex items-center justify-center">
                        {getServiceIcon(item.iconName)}
                      </div>
                      <Badge variant="secondary" className="text-xs">
                        {item.badge}
                      </Badge>
                    </div>
                    <CardTitle className="text-lg font-semibold text-card-foreground group-hover:text-primary transition-colors font-default">
                      {item.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pb-6">
                    <CardDescription className="text-muted-foreground text-sm leading-relaxed font-default">
                      {item.description}
                    </CardDescription>
                  </CardContent>
                  <CardFooter className="pt-0">
                    <Button asChild variant="ghost" size="sm" className="w-full justify-between px-2 text-foreground hover:text-primary hover:bg-muted">
                      <Link to={item.to} className="flex items-center w-full justify-between">
                        <span>View details</span>
                        <ArrowRight className="size-4 ml-1.5 transition-transform group-hover:translate-x-1" />
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </motion.div>
            </div>)}
        </div>

        {/* CTA Quote Banner */}
        <motion.div initial={shouldReduceMotion ? false : {
        opacity: 0,
        y: 16
      }} whileInView={shouldReduceMotion ? undefined : {
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.3
      }} transition={{
        duration: 0.5
      }} className="rounded-xl border border-border bg-secondary text-secondary-foreground p-6 sm:p-8 lg:p-10 shadow-md">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
            <div className="space-y-2 max-w-2xl">
              <div className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                <ShieldCheck className="size-4 text-primary" />
                <span>NASC &amp; CHAS Accredited</span>
              </div>
              <h3 className="text-xl sm:text-2xl font-semibold text-secondary-foreground font-default">
                Need a clear, itemised scaffolding quotation?
              </h3>
              <p className="text-sm sm:text-base text-muted-foreground font-default">
                Speak directly with Pete Wood and our operational team for site visits, drawings, and rapid turnaround quotes.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full lg:w-auto shrink-0">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm font-medium">
                <Link to="/ContactRequestQuotePage">
                  <span>Request a quote</span>
                  <ArrowRight className="size-4 ml-2" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="border-sidebar-border bg-card/10 text-secondary-foreground hover:bg-card/20 hover:text-secondary-foreground font-medium">
                <Link to="/AboutBmfPage">
                  <span>About BMF</span>
                </Link>
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}